<template>
  <div class="w-full">
    <div :id="elementId"></div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onBeforeUnmount, onMounted, watch } from 'vue';
import loadScript from 'load-script';
import { getAuthToken } from '@/services/authToken';

type PayerInfo = {
  firstName?: string | null;
  lastName?: string | null;
};

type MercadoPagoBrickController = {
  unmount?: () => void;
  destroy?: () => void;
};

type MercadoPagoBrickSettings = {
  initialization: {
    amount: number;
    currency_id: string;
    payer: {
      firstName: string;
      lastName: string;
      email: string;
      entityType: string;
    };
  };
  customization: {
    visual: {
      style: {
        theme: string;
      };
    };
    paymentMethods: {
      creditCard: string;
      debitCard: string;
      maxInstallments: number;
    };
  };
  callbacks: {
    onReady: () => void;
    onSubmit: (payload: { formData: Record<string, unknown> }) => Promise<unknown>;
    onError: (error: { message?: string }) => void;
  };
  saveCardToken: boolean;
};

type MercadoPagoBrickBuilder = {
  create: (
    type: string,
    elementId: string,
    settings: MercadoPagoBrickSettings,
  ) => Promise<MercadoPagoBrickController>;
};

type MercadoPagoInstance = {
  bricks: () => MercadoPagoBrickBuilder;
};

type MercadoPagoSdk = new (
  publicKey: string,
  options: { locale?: string },
) => MercadoPagoInstance;

declare global {
  interface Window {
    MercadoPago?: MercadoPagoSdk;
  }
}

let mercadoPagoPromise: Promise<MercadoPagoSdk> | null = null;

const loadMercadoPagoSdk = (scriptUrl?: string): Promise<MercadoPagoSdk> => {
  if (!mercadoPagoPromise) {
    mercadoPagoPromise = new Promise((resolve, reject) => {
      loadScript(scriptUrl || 'https://sdk.mercadopago.com/js/v2', (err) => {
        if (err) {
          mercadoPagoPromise = null;
          reject(err);
          return;
        }
        if (window.MercadoPago) {
          resolve(window.MercadoPago);
        } else {
          mercadoPagoPromise = null;
          reject(new Error('MercadoPago SDK not available on window'));
        }
      });
    });
  }

  return mercadoPagoPromise;
};

const defaultEndpoint = () => {
  const base = process.env.VUE_APP_MEDKUS_BACKEND_URL;
  if (typeof base === 'string' && base.length) {
    const sanitized = base.replace(/\/$/, '');
    return `${sanitized}/api/publicmercadopago/process_payment`;
  }
  return '/api/publicmercadopago/process_payment';
};

export default defineComponent({
  name: 'MercadoPagoForm',
  props: {
    requestId: {
      type: [Number, String],
      required: true,
    },
    amount: {
      type: Number,
      required: true,
    },
    currency: {
      type: String,
      required: true,
    },
    email: {
      type: String,
      required: true,
    },
    payer: {
      type: Object as () => PayerInfo,
      default: () => ({}),
    },
    publicKey: {
      type: String,
      required: true,
    },
    locale: {
      type: String,
      default: 'es-AR',
    },
    saveCardToken: {
      type: Boolean,
      default: false,
    },
    endpoint: {
      type: String,
      default: '',
    },
  },
  emits: ['success', 'error', 'loading'],
  setup(props, { emit }) {
    const elementId = `mercadopago-brick-${Math.random().toString(36).slice(2, 11)}`;
    let brickController: MercadoPagoBrickController | null = null;

    const resolveEndpoint = () => {
      if (props.endpoint && props.endpoint.trim().length) {
        return props.endpoint;
      }
      return defaultEndpoint();
    };

    const cleanupBrick = () => {
      if (brickController?.unmount) {
        brickController.unmount();
      } else if (brickController?.destroy) {
        brickController.destroy();
      }
      brickController = null;
    };

    const submitPayment = async (formData: Record<string, unknown>) => {
      emit('loading', true);
      try {
        const payload = {
          ...formData,
          rqId: props.requestId,
          saveCardToken: props.saveCardToken,
        };

        const token = getAuthToken();
        const response = await fetch(resolveEndpoint(), {
          method: 'POST',
          // Cookie de sesión HttpOnly (api.medkus.com).
          credentials: 'include',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          const text = await response.text();
          throw new Error(text || 'Unable to reach MercadoPago.');
        }

        const data = await response.json();
        if (data?.ok) {
          emit('success', data);
          return data;
        }
        throw new Error(data?.errorMsg || 'Unable to complete payment');
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        emit('error', message);
        throw error;
      } finally {
        emit('loading', false);
      }
    };

    const renderBrick = async () => {
      cleanupBrick();

      if (!props.publicKey) {
        emit('error', 'MercadoPago public key is not configured.');
        return;
      }

      emit('loading', true);

      try {
        const sdkUrl = process.env.VUE_APP_MERCADOPAGO_INSITE_SCRIPT;
        const MercadoPago = await loadMercadoPagoSdk(sdkUrl);
        const mp = new MercadoPago(props.publicKey, {
          locale: props.locale || 'es-AR',
        });
        const bricksBuilder = mp.bricks();

        const settings: MercadoPagoBrickSettings = {
          initialization: {
            amount: props.amount,
            currency_id: props.currency,
            payer: {
              firstName: props.payer?.firstName || '',
              lastName: props.payer?.lastName || '',
              email: props.email,
              entityType: 'individual',
            },
          },
          customization: {
            visual: {
              style: {
                theme: 'default',
              },
            },
            paymentMethods: {
              creditCard: 'all',
              debitCard: 'all',
              maxInstallments: 1,
            },
          },
          callbacks: {
            onReady: () => {
              emit('loading', false);
            },
            onSubmit: ({ formData }: { formData: Record<string, unknown> }) => {
              return submitPayment(formData);
            },
            onError: (error: { message?: string }) => {
              const message = error?.message || 'MercadoPago error';
              emit('error', message);
            },
          },
          saveCardToken: props.saveCardToken,
        };

        brickController = await bricksBuilder.create('payment', elementId, settings);
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        emit('error', message);
        emit('loading', false);
      }
    };

    onMounted(() => {
      renderBrick();
    });

    onBeforeUnmount(() => {
      cleanupBrick();
    });

    watch(
      () => [props.requestId, props.amount, props.currency, props.publicKey, props.email],
      () => {
        renderBrick();
      }
    );

    return {
      elementId,
    };
  },
});
</script>
